/**
 * Site Header
 *
 * Shared public header for the marketing surface.
 *
 * Primary responsibilities:
 * - Render the public navigation shell
 * - Keep route actions and mobile navigation consistent
 *
 * Dependencies:
 * - marketing/site-map
 * - ui/button
 */

'use client'

import { useMemo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import { Menu } from 'lucide-react'

import { buildMarketingPrimaryNavItems } from '@/components/marketing/site-map'
import { getMarketingCopy } from '@/lib/marketing-copy'
import { Button } from '@/components/ui/button'
import { HeaderLosiMark } from '@/components/losi-bot/header-losi-mark'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

function isActivePath(pathname: string, href: string) {
  if (href === '/') {
    return pathname === '/'
  }

  return pathname === href || pathname.startsWith(`${href}/`)
}

export function SiteHeader() {
  const pathname = usePathname()
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)
  const marketingPrimaryNavItems = useMemo(
    () => buildMarketingPrimaryNavItems(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  )

  const activeHref = useMemo(() => {
    return marketingPrimaryNavItems.find((item) => isActivePath(pathname, item.href))?.href
  }, [marketingPrimaryNavItems, pathname])

  return (
    <header className="sticky top-0 z-50 -mb-4 px-4 pb-4">
      {/* launch-ui style: full-width blur bar that fades out toward its bottom */}
      <div className="fade-bottom bg-background/15 absolute left-0 top-0 h-20 w-full backdrop-blur-lg" />
      <div className="relative mx-auto max-w-[1400px]">
        <div className="flex items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-6">
            <Link href="/" className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-2xl border border-slate-200/80 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] dark:border-white/10 dark:bg-white/5 dark:shadow-none">
                <HeaderLosiMark size={26} />
              </div>
              <span className="text-base font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                Losi
              </span>
            </Link>

            <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
              {marketingPrimaryNavItems.map((item) => {
                const active = item.href === activeHref

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      'rounded-full px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:text-slate-950 dark:text-slate-300 dark:hover:text-white',
                      active && 'text-slate-950 dark:text-white'
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Button
              asChild
              variant="ghost"
              className="hidden rounded-full px-4 text-slate-700 hover:bg-slate-100 hover:text-slate-950 md:inline-flex dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <Link href="/login">{copy.common.logIn}</Link>
            </Button>
            {/* Keep the primary signup/open CTA visible on mobile — hiding it
                behind the hamburger was killing homepage conversion. */}
            <Button
              asChild
              className="rounded-full bg-slate-950 px-4 text-white hover:bg-slate-800 sm:px-5 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
            >
              <Link href="/login?register=true">
                {copy.common.startFree}
                <ArrowRightIcon className="ml-2 size-4" />
              </Link>
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0 md:hidden">
                  <Menu className="size-5" />
                  <span className="sr-only">Toggle navigation menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <nav className="mt-6 grid gap-2">
                  {marketingPrimaryNavItems.map((item) => {
                    const active = item.href === activeHref

                    return (
                      <SheetClose asChild key={item.href}>
                        <Link
                          href={item.href}
                          className={cn(
                            'rounded-[1.25rem] px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white',
                            active && 'bg-slate-100 text-slate-950 dark:bg-white/10 dark:text-white'
                          )}
                          aria-current={active ? 'page' : undefined}
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    )
                  })}
                </nav>
                <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 dark:border-white/10">
                  <SheetClose asChild>
                    <Button
                      asChild
                      variant="outline"
                      className="h-11 w-full rounded-full border-slate-300 bg-white dark:border-white/15 dark:bg-white/5 dark:text-white"
                    >
                      <Link href="/login">{copy.common.logIn}</Link>
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button
                      asChild
                      className="h-11 w-full rounded-full bg-slate-950 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                    >
                      <Link href="/login?register=true">
                        {copy.common.startFree}
                        <ArrowRightIcon className="ml-2 size-4" />
                      </Link>
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  )
}
